export type Role =
  | 'super_admin'
  | 'state_admin'
  | 'district_coordinator'
  | 'moderator'
  | 'committee'
  | 'imam'
  | 'volunteer'
  | 'donor'
  | 'waqf_officer'
  | 'contributor'
  | 'member'
  | 'public'

export type Permission =
  | 'platform.view'
  | 'platform.settings'
  | 'platform.features'
  | 'admin.access'
  | 'analytics.view'
  | 'users.view'
  | 'users.manage'
  | 'users.assign_roles'
  | 'masjids.view'
  | 'masjids.create'
  | 'masjids.edit'
  | 'masjids.approve'
  | 'masjids.verify'
  | 'masjids.delete'
  | 'dashboard.view'
  | 'dashboard.manage'
  | 'announcements.view'
  | 'announcements.manage'
  | 'campaigns.view'
  | 'campaigns.create'
  | 'campaigns.manage'
  | 'campaigns.approve'
  | 'donations.view'
  | 'donations.manage'
  | 'finance.view'
  | 'finance.manage'
  | 'staff.view'
  | 'staff.manage'
  | 'meetings.view'
  | 'meetings.manage'
  | 'content.view'
  | 'content.create'
  | 'content.moderate'
  | 'businesses.view'
  | 'businesses.manage'
  | 'businesses.approve'
  | 'volunteers.view'
  | 'volunteers.manage'
  | 'volunteers.submit'
  | 'waqf.view'
  | 'waqf.create'
  | 'waqf.edit'
  | 'waqf.approve'
  | 'waqf.legal'
  | 'waqf.confidential'
  | 'waqf.evidence'
  | 'waqf.gazette'
  | 'ai.use'
  | 'ai.admin'
  | 'ai.waqf'
  | 'ai.finance'
  | 'ai.moderation'
  | 'notifications.view'
  | 'notifications.manage'
  // Restricted: send an ATTRIBUTED "official" (verified-body) message or a "safety" alert.
  // Tighter than notifications.manage — granted to super_admin ONLY for now (provision below
  // to extend to other designated senders later).
  | 'notifications.official'

export type WorkspaceScope = 'platform' | 'state' | 'district' | 'masjid' | 'waqf' | 'personal' | 'public'

export interface RoleScope {
  workspaceScopes: WorkspaceScope[]
  description: string
}

export const ROLE_SCOPES: Record<Role, RoleScope> = {
  super_admin: {
    workspaceScopes: ['platform', 'state', 'district', 'masjid', 'waqf', 'personal'],
    description: 'Full platform access across all states, districts, masjids, waqf records, users, and settings.',
  },
  state_admin: {
    workspaceScopes: ['state', 'district', 'masjid', 'waqf'],
    description: 'State-level operator with access restricted to assigned state workspaces.',
  },
  district_coordinator: {
    workspaceScopes: ['district', 'masjid'],
    description: 'District-level coordinator for local masjid verification, volunteers, and moderation queues.',
  },
  moderator: {
    workspaceScopes: ['platform', 'state', 'district'],
    description: 'Content and listing reviewer with limited admin access.',
  },
  committee: {
    workspaceScopes: ['masjid'],
    description: 'Masjid committee dashboard operator for the assigned masjid workspace.',
  },
  imam: {
    workspaceScopes: ['masjid'],
    description: 'Religious staff role for announcements, prayer updates, and community content.',
  },
  volunteer: {
    workspaceScopes: ['district', 'personal'],
    description: 'Volunteer contributor for local masjid submissions and verification tasks.',
  },
  donor: {
    workspaceScopes: ['personal'],
    description: 'Donor/member account scope for donations, receipts, follows, and notifications.',
  },
  waqf_officer: {
    workspaceScopes: ['waqf', 'masjid', 'district', 'state'],
    description: 'Waqf protection and legal operations role with confidential-property access when granted.',
  },
  contributor: {
    workspaceScopes: ['personal'],
    description: 'Article/content contributor with access to own submissions.',
  },
  member: {
    workspaceScopes: ['personal'],
    description: 'Authenticated community member.',
  },
  public: {
    workspaceScopes: ['public'],
    description: 'Unauthenticated public visitor.',
  },
}

export const PERMISSION_MATRIX: Record<Role, Permission[]> = {
  super_admin: [
    'platform.view', 'platform.settings', 'platform.features', 'admin.access', 'analytics.view',
    'users.view', 'users.manage', 'users.assign_roles',
    'masjids.view', 'masjids.create', 'masjids.edit', 'masjids.approve', 'masjids.verify', 'masjids.delete',
    'dashboard.view', 'dashboard.manage',
    'announcements.view', 'announcements.manage',
    'campaigns.view', 'campaigns.create', 'campaigns.manage', 'campaigns.approve',
    'donations.view', 'donations.manage', 'finance.view', 'finance.manage',
    'staff.view', 'staff.manage', 'meetings.view', 'meetings.manage',
    'content.view', 'content.create', 'content.moderate',
    'businesses.view', 'businesses.manage', 'businesses.approve',
    'volunteers.view', 'volunteers.manage', 'volunteers.submit',
    'waqf.view', 'waqf.create', 'waqf.edit', 'waqf.approve', 'waqf.legal', 'waqf.confidential', 'waqf.evidence', 'waqf.gazette',
    'ai.use', 'ai.admin', 'ai.waqf', 'ai.finance', 'ai.moderation',
    'notifications.view', 'notifications.manage', 'notifications.official',
  ],
  state_admin: [
    'platform.view', 'admin.access', 'analytics.view', 'users.view',
    'masjids.view', 'masjids.edit', 'masjids.approve', 'masjids.verify',
    'campaigns.view', 'campaigns.approve', 'businesses.view', 'businesses.approve',
    'volunteers.view', 'volunteers.manage',
    'waqf.view', 'waqf.approve', 'waqf.evidence', 'waqf.gazette',
    'ai.use', 'ai.moderation', 'notifications.view',
  ],
  district_coordinator: [
    'dashboard.view', 'masjids.view', 'masjids.create', 'masjids.edit', 'masjids.verify',
    'volunteers.view', 'volunteers.manage', 'campaigns.view', 'businesses.view',
    'ai.use', 'notifications.view',
  ],
  moderator: [
    'admin.access', 'platform.view', 'masjids.view', 'masjids.approve', 'masjids.verify',
    'campaigns.view', 'campaigns.approve',
    'content.view', 'content.moderate',
    'businesses.view', 'businesses.approve',
    'volunteers.view', 'volunteers.manage',
    'waqf.view', 'ai.use', 'ai.moderation', 'notifications.view',
  ],
  committee: [
    'dashboard.view', 'dashboard.manage',
    'masjids.view', 'masjids.edit',
    'announcements.view', 'announcements.manage',
    'campaigns.view', 'campaigns.create', 'campaigns.manage',
    'donations.view', 'donations.manage',
    'finance.view', 'finance.manage',
    'staff.view', 'staff.manage',
    'meetings.view', 'meetings.manage',
    'waqf.view', 'waqf.create', 'waqf.evidence',
    'ai.use', 'ai.finance', 'notifications.view', 'notifications.manage',
  ],
  imam: [
    'dashboard.view', 'announcements.view', 'announcements.manage',
    'meetings.view', 'content.view', 'content.create',
    'masjids.view', 'ai.use', 'notifications.view',
  ],
  volunteer: [
    'dashboard.view', 'masjids.view', 'masjids.create',
    'volunteers.submit', 'notifications.view',
  ],
  donor: [
    'campaigns.view', 'donations.view', 'notifications.view',
  ],
  waqf_officer: [
    'dashboard.view', 'admin.access',
    'waqf.view', 'waqf.create', 'waqf.edit', 'waqf.approve', 'waqf.legal', 'waqf.confidential', 'waqf.evidence', 'waqf.gazette',
    'masjids.view', 'finance.view', 'ai.use', 'ai.waqf', 'notifications.view', 'notifications.manage',
  ],
  contributor: [
    'dashboard.view', 'content.view', 'content.create', 'notifications.view',
  ],
  member: [
    'campaigns.view', 'donations.view', 'notifications.view',
  ],
  public: [],
}

export const ROLE_ALIASES: Record<string, Role> = {
  admin: 'super_admin',
  masjid_admin: 'committee',
  'masjid-admin': 'committee',
  business_owner: 'member',
}

export const PLATFORM_ADMIN_ROLES: Role[] = ['super_admin', 'state_admin']
export const PLATFORM_REVIEW_ROLES: Role[] = ['super_admin', 'state_admin', 'district_coordinator', 'moderator']
/* Content-moderation tier: platform admins + moderator. ONE shared constant used on BOTH the LIST and
   the APPROVE/DECLINE of every moderation queue (web + mobile), AND in middleware to admit a
   moderator's mutating /api/admin/* moderation calls + the moderation admin PAGES. It grants NO admin
   powers: users/roles, feature flags, settings, subscriptions/tiers, discovery, imports, and masjid
   delete stay platform-admin-only, enforced by their own route guards and by the default-deny
   middleware gate (a path not on the moderation allow-list still requires a platform admin). It does
   NOT add district_coordinator — that role's mutating-route access is unchanged by this fix. */
export const MODERATION_ROLES: Role[] = ['super_admin', 'state_admin', 'moderator']
export const MASJID_OPERATOR_ROLES: Role[] = ['super_admin', 'state_admin', 'district_coordinator', 'moderator', 'committee', 'imam']
export const MASJID_MANAGEMENT_ROLES: Role[] = ['super_admin', 'state_admin', 'district_coordinator', 'moderator', 'committee']
export const MASJID_FINANCE_ROLES: Role[] = ['super_admin', 'state_admin', 'district_coordinator', 'moderator', 'committee']
export const MASJID_NOTIFICATION_MANAGER_ROLES: Role[] = ['super_admin', 'state_admin', 'district_coordinator', 'moderator', 'committee', 'imam', 'waqf_officer']

export function isPlatformAdminRole(role: Role): boolean {
  return PLATFORM_ADMIN_ROLES.includes(role)
}

export function isPlatformReviewerRole(role: Role): boolean {
  return PLATFORM_REVIEW_ROLES.includes(role)
}

export function isModerationRole(role: Role): boolean {
  return MODERATION_ROLES.includes(role)
}

export function isMasjidOperatorRole(role: Role): boolean {
  return MASJID_OPERATOR_ROLES.includes(role)
}

export function normalizeRole(role?: string | null): Role {
  if (!role) return 'member'
  const normalized = role.trim().toLowerCase().replace(/\s+/g, '_')
  if (normalized in ROLE_ALIASES) return ROLE_ALIASES[normalized]
  if (normalized in PERMISSION_MATRIX) return normalized as Role
  return 'member'
}

export function getRolePermissions(role: Role): Permission[] {
  return PERMISSION_MATRIX[role] || []
}

export function hasPermission(role: Role, permission: Permission): boolean {
  return getRolePermissions(role).includes(permission)
}

export function hasAnyPermission(role: Role, permissions: Permission[]): boolean {
  return permissions.some((permission) => hasPermission(role, permission))
}

export function hasRole(role: Role, allowedRoles: Role[]): boolean {
  return allowedRoles.includes(role)
}
